import React, { useState, useEffect } from 'react';
import { ArrowLeft, Bug, Lightbulb, MessageSquare, Clock, CheckCircle, AlertTriangle, Plus, Home } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabase';
import toast from 'react-hot-toast';
import FeedbackForm from './FeedbackForm';

interface FeedbackItem {
  id: string;
  type: 'bug' | 'feature';
  title: string;
  description: string;
  status: 'pending' | 'in_progress' | 'completed';
  created_at: string;
  updated_at: string;
  comments: FeedbackComment[];
}

interface FeedbackComment {
  id: string;
  feedback_id: string;
  user_id: string;
  comment: string;
  created_at: string;
}

export default function Feedback() {
  const { user } = useAuth();
  const [items, setItems] = useState<FeedbackItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selectedItem, setSelectedItem] = useState<FeedbackItem | null>(null);
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'in_progress' | 'completed'>('all');

  useEffect(() => {
    loadFeedbackItems();
  }, []);

  const loadFeedbackItems = async () => {
    try {
      const { data: feedbackData, error: feedbackError } = await supabase
        .from('feedback_items')
        .select('*')
        .order('created_at', { ascending: false });

      if (feedbackError) throw feedbackError;

      const { data: commentsData, error: commentsError } = await supabase
        .from('feedback_comments')
        .select('*')
        .order('created_at', { ascending: true });

      if (commentsError) throw commentsError;

      const itemsWithComments = feedbackData.map((item: FeedbackItem) => ({
        ...item,
        comments: commentsData.filter((comment: FeedbackComment) => comment.feedback_id === item.id)
      }));

      setItems(itemsWithComments);
    } catch (error) {
      console.error('Error loading feedback:', error);
      toast.error('Failed to load feedback items');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (itemId: string, newStatus: 'pending' | 'in_progress' | 'completed') => {
    try {
      const { error } = await supabase
        .from('feedback_items')
        .update({ status: newStatus })
        .eq('id', itemId);

      if (error) throw error;

      setItems(prev => prev.map(item => 
        item.id === itemId ? { ...item, status: newStatus } : item
      ));

      toast.success('Status updated successfully');
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    }
  };

  const handleAddComment = async (itemId: string, comment: string) => {
    try {
      const { data, error } = await supabase
        .from('feedback_comments')
        .insert({
          feedback_id: itemId,
          user_id: user?.id,
          comment
        })
        .select()
        .single();

      if (error) throw error;

      setItems(prev => prev.map(item => 
        item.id === itemId 
          ? { ...item, comments: [...item.comments, data] }
          : item
      ));

      toast.success('Comment added successfully');
    } catch (error) {
      console.error('Error adding comment:', error);
      toast.error('Failed to add comment');
    }
  };

  const filteredBugs = items.filter(item => 
    item.type === 'bug' && 
    (statusFilter === 'all' || item.status === statusFilter)
  );

  const filteredFeatures = items.filter(item => 
    item.type === 'feature' && 
    (statusFilter === 'all' || item.status === statusFilter)
  );

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <Clock className="h-4 w-4" />;
      case 'in_progress':
        return <AlertTriangle className="h-4 w-4" />;
      case 'completed':
        return <CheckCircle className="h-4 w-4" />;
      default:
        return null;
    }
  };

  const FeedbackCard = ({ item }: { item: FeedbackItem }) => (
    <div className="border border-gray-200 rounded-lg p-6 mb-4">
      <div className="flex items-start justify-between">
        <div className="flex items-start space-x-3">
          {item.type === 'bug' ? (
            <Bug className="h-5 w-5 text-red-500 mt-1" />
          ) : (
            <Lightbulb className="h-5 w-5 text-yellow-500 mt-1" />
          )}
          <div>
            <h3 className="text-lg font-medium text-gray-900">
              {item.title}
            </h3>
            <p className="mt-1 text-sm text-gray-600">
              {item.description}
            </p>
          </div>
        </div>
        {user?.email === 'steve@shadowtek.com.au' && (
          <select
            value={item.status}
            onChange={(e) => handleStatusChange(item.id, e.target.value as 'pending' | 'in_progress' | 'completed')}
            className="ml-4 rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500"
          >
            <option value="pending">Pending</option>
            <option value="in_progress">In Progress</option>
            <option value="completed">Completed</option>
          </select>
        )}
      </div>

      <div className="mt-4 flex items-center space-x-4">
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(item.status)}`}>
          {getStatusIcon(item.status)}
          <span className="ml-1 capitalize">{item.status.replace('_', ' ')}</span>
        </span>
        <span className="text-sm text-gray-500">
          {new Date(item.created_at).toLocaleDateString()}
        </span>
      </div>

      {item.comments.length > 0 && (
        <div className="mt-4 space-y-3">
          {item.comments.map((comment) => (
            <div key={comment.id} className="flex items-start space-x-3 bg-gray-50 rounded-lg p-3">
              <MessageSquare className="h-4 w-4 text-gray-400 mt-1" />
              <div>
                <p className="text-sm text-gray-600">{comment.comment}</p>
                <span className="text-xs text-gray-500">
                  {new Date(comment.created_at).toLocaleString()}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-4">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            const form = e.target as HTMLFormElement;
            const comment = (form.elements.namedItem('comment') as HTMLInputElement).value;
            if (comment.trim()) {
              handleAddComment(item.id, comment);
              form.reset();
            }
          }}
          className="flex items-start space-x-3"
        >
          <input
            type="text"
            name="comment"
            placeholder="Add a comment..."
            className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500"
          />
          <button
            type="submit"
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-orange-600 hover:bg-orange-700"
          >
            Comment
          </button>
        </form>
      </div>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <Link
              to="/dashboard"
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
            >
              <Home className="h-5 w-5 mr-2" />
              Dashboard
            </Link>
            <span className="text-gray-400">/</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Feedback</h1>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-orange-600 hover:bg-orange-700"
        >
          <Plus className="h-5 w-5 mr-2" />
          New Feedback
        </button>
      </div>

      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="p-6">
          <div className="flex justify-end mb-6">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as 'all' | 'pending' | 'in_progress' | 'completed')}
              className="rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500"
            >
              <option value="all">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
            </select>
          </div>

          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No feedback items found.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Bugs Column */}
              <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Bug className="h-5 w-5 text-red-500 mr-2" />
                  Bug Reports
                  <span className="ml-2 text-sm text-gray-500">({filteredBugs.length})</span>
                </h2>
                <div className="space-y-4">
                  {filteredBugs.map((item) => (
                    <FeedbackCard key={item.id} item={item} />
                  ))}
                  {filteredBugs.length === 0 && (
                    <p className="text-center text-gray-500 py-4">No bug reports found.</p>
                  )}
                </div>
              </div>

              {/* Features Column */}
              <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Lightbulb className="h-5 w-5 text-yellow-500 mr-2" />
                  Feature Requests
                  <span className="ml-2 text-sm text-gray-500">({filteredFeatures.length})</span>
                </h2>
                <div className="space-y-4">
                  {filteredFeatures.map((item) => (
                    <FeedbackCard key={item.id} item={item} />
                  ))}
                  {filteredFeatures.length === 0 && (
                    <p className="text-center text-gray-500 py-4">No feature requests found.</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {showForm && (
        <FeedbackForm
          onClose={() => setShowForm(false)}
          onSubmit={async (data) => {
            try {
              const { error } = await supabase
                .from('feedback_items')
                .insert({
                  user_id: user?.id,
                  type: data.type,
                  title: data.title,
                  description: data.description
                });

              if (error) throw error;

              toast.success('Feedback submitted successfully');
              setShowForm(false);
              loadFeedbackItems();
            } catch (error) {
              console.error('Error submitting feedback:', error);
              toast.error('Failed to submit feedback');
            }
          }}
        />
      )}
    </div>
  );
}